import {
  Box,
  Center,
  Flex,
  Group,
  Indicator,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import * as df from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { client } from "@/api-client";
import type { GroupChatMessage } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { GroupLogo } from "@/components/GroupLogo";
import { Portrait } from "@/components/Portrait";
import { useHashTab } from "@/hooks/useHashTab";
import { UniversalRoutes, useNavigator } from "@/routers/routes";
import { type Role, useUser } from "@/UserContext";
import { type ChatMessage, useWebSocket } from "@/websocket/client";

// Shared function for merging and sorting messages
const mergeAndSortMessages = <
  T extends { lastMessage?: { sentAt: string } | null },
>(
  items: T[] | undefined,
  messagesWithUpdates: Record<string, ChatMessage | GroupChatMessage>,
  idKey: keyof T,
): T[] => {
  if (!items) {
    return [];
  }

  const updatedItems = items.map((item) => {
    const itemId = item[idKey] as string;
    const potentialLastMessage = messagesWithUpdates[itemId];
    if (potentialLastMessage) {
      return {
        ...item,
        lastMessage: potentialLastMessage,
      };
    }
    return item;
  });

  return updatedItems.sort(
    (a, b) =>
      new Date(b.lastMessage?.sentAt ?? 0).getTime() -
      new Date(a.lastMessage?.sentAt ?? 0).getTime(),
  );
};

// Shared date formatting function
const formatDate = (dateStr: string) => {
  const date = df.parseISO(dateStr);

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (df.isSameDay(date, today)) {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  if (df.isSameDay(date, yesterday)) {
    return "Yesterday";
  }
  return date.toLocaleDateString();
};

const renderMessagePreview = (message: {
  specialType?: "CANCEL_SESSION" | "FILE" | null;
  content?: string | null;
}) => {
  if (message.specialType === "FILE") {
    return "Sent a file";
  }
  return message.content;
};

// Individual chat list component
const IndividualChatList = ({ whoAmI }: { whoAmI: Role }) => {
  const user = useUser();
  const { lastMessage: lastMessageRaw } = useWebSocket();
  const { navigator } = useNavigator();

  // Individual chats
  const { data: chats, isLoading } = client.useQuery(
    "get",
    "/universal/chats/",
  );

  const [chatsWithLastMessage, setChatsWithLastMessage] = useState<
    Record<string, ChatMessage>
  >({});

  const lastMessage =
    lastMessageRaw &&
    lastMessageRaw.type === "chat_message" &&
    lastMessageRaw.content;

  useEffect(() => {
    if (!lastMessage) {
      return;
    }

    // Update individual chats
    setChatsWithLastMessage((prev) => ({
      ...prev,
      [lastMessage.engagementId]: lastMessage,
    }));
  }, [lastMessage]);

  // Update chats with lastMessage from websocket if available
  const updatedChats = useMemo(() => {
    return mergeAndSortMessages(
      chats?.chats,
      chatsWithLastMessage,
      "engagementId",
    );
  }, [chats, chatsWithLastMessage]);

  if (isLoading) {
    return <FancyLoader />;
  }

  const showIndicator = (chat: (typeof updatedChats)[number]) => {
    const lastMessage = chat.lastMessage;
    if (!lastMessage) {
      return false;
    }

    if (lastMessage.senderId === user.accountId) {
      return false;
    }

    return (
      (whoAmI === "owner" && lastMessage.sentAt > chat.lastSeenAtOwner) ||
      (whoAmI === "trainer" && lastMessage.sentAt > chat.lastSeenAtTrainer)
    );
  };

  const chatListContent =
    updatedChats.length > 0 ? (
      <Stack>
        {updatedChats.map((chat) => (
          <Paper
            key={chat.engagementId}
            onClick={navigator({
              path: UniversalRoutes.CHAT.DETAIL,
              params: { engagementId: chat.engagementId },
            })}
          >
            <Flex gap="sm" align="center">
              <Indicator
                color="dark-accent.3"
                position="top-end"
                size={8}
                offset={0}
                disabled={!showIndicator(chat)}
              >
                <Portrait
                  profile={whoAmI === "trainer" ? chat.owner : chat.trainer}
                  size="lg"
                />
              </Indicator>
              <Box
                style={{
                  flex: "1 1 auto",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                <Group justify="space-between">
                  <Text fw="bold">
                    {whoAmI === "trainer"
                      ? chat.owner.firstName
                      : chat.trainer.firstName}
                  </Text>
                  {chat.lastMessage && (
                    <Text size="sm" c="gray.8">
                      {formatDate(chat.lastMessage.sentAt)}
                    </Text>
                  )}
                </Group>
                {chat.lastMessage && (
                  <Text
                    c="gray.8"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {chat.lastMessage.senderId === user.accountId && "You: "}
                    {renderMessagePreview(chat.lastMessage)}
                  </Text>
                )}
              </Box>
            </Flex>
          </Paper>
        ))}
      </Stack>
    ) : (
      <Center>
        <Paper p="md" radius="lg" withBorder>
          <Text ta="center">
            You will be able to communicate with clients once they sign up and
            connect with you.
          </Text>
        </Paper>
      </Center>
    );

  return chatListContent;
};

// Group chat list component
const GroupChatList = () => {
  const user = useUser();
  const { lastMessage: lastMessageRaw } = useWebSocket();
  const { navigator } = useNavigator();

  // Group chats
  const { data: groupChats, isLoading } = client.useQuery(
    "get",
    "/universal/groups/chat",
  );

  const [groupsWithLastMessage, setGroupsWithLastMessage] = useState<
    Record<string, GroupChatMessage>
  >({});

  const lastGroupMessage =
    lastMessageRaw &&
    lastMessageRaw.type === "group_chat_message" &&
    lastMessageRaw.content;

  useEffect(() => {
    if (!lastGroupMessage) {
      return;
    }

    // Update group chats
    setGroupsWithLastMessage((prev) => ({
      ...prev,
      [lastGroupMessage.groupId]: lastGroupMessage,
    }));
  }, [lastGroupMessage]);

  // Update group chats with lastMessage from websocket
  const updatedGroupChats = useMemo(() => {
    return mergeAndSortMessages(
      groupChats?.groups,
      groupsWithLastMessage,
      "groupId",
    );
  }, [groupChats, groupsWithLastMessage]);

  if (isLoading) {
    return <FancyLoader />;
  }

  const showIndicator = (group: (typeof updatedGroupChats)[number]) => {
    const lastMessage = group.lastMessage;
    if (!lastMessage) {
      return false;
    }

    if (lastMessage.senderId === user.accountId) {
      return false;
    }

    return lastMessage.sentAt > group.lastSeenAt;
  };

  const groupChatListContent = updatedGroupChats.length ? (
    <Stack>
      {updatedGroupChats.map((group) => (
        <Paper
          key={group.groupId}
          onClick={navigator({
            path: UniversalRoutes.GROUP_CHAT.DETAIL,
            params: { groupId: group.groupId },
          })}
        >
          <Flex gap="sm" align="center">
            <Indicator
              color="dark-accent.3"
              position="top-end"
              size={8}
              offset={0}
              disabled={!showIndicator(group)}
            >
              <GroupLogo name={group.name} />
            </Indicator>
            <Box
              style={{
                flex: "1 1 auto",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              <Group justify="space-between">
                <Text fw="bold">{group.name}</Text>
                {group.lastMessage && (
                  <Text size="sm" c="gray.8">
                    {formatDate(group.lastMessage.sentAt)}
                  </Text>
                )}
              </Group>
              {group.lastMessage && (
                <Text
                  c="gray.8"
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {group.lastMessage.senderId === user.accountId && "You: "}
                  {renderMessagePreview(group.lastMessage)}
                </Text>
              )}
            </Box>
          </Flex>
        </Paper>
      ))}
    </Stack>
  ) : (
    <Center>
      <Paper p="md" radius="lg" withBorder>
        <Text ta="center">No group chats available yet.</Text>
      </Paper>
    </Center>
  );

  return groupChatListContent;
};

// Main component with tabs
export const ChatsWithTabs = ({ whoAmI }: { whoAmI: Role }) => {
  const { activeTab, onTabChange } = useHashTab<"individual" | "groups">(
    "individual",
  );

  return (
    <Stack>
      <Title order={1}>Messages</Title>
      <Tabs
        value={activeTab}
        onChange={(value) =>
          value && onTabChange(value as "individual" | "groups")
        }
      >
        <Tabs.List grow>
          <Tabs.Tab value="individual">
            {whoAmI === "trainer" ? "Clients" : "Trainers"}
          </Tabs.Tab>
          <Tabs.Tab value="groups">Groups</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="individual" pt="md">
          <IndividualChatList whoAmI={whoAmI} />
        </Tabs.Panel>

        <Tabs.Panel value="groups" pt="md">
          <GroupChatList />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};
